import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS } from '@/lib/mocks';
import { useTenant } from '@/modules/tenant/presentation/hooks/use-tenant';
import type { AnswerKeyEntry } from '@/modules/diagnostico/domain/entities/answer-key.entity';
import { toAnswerKey } from '@/modules/simulado/domain/entities/question.entity';
import { AnswerKeyApiDatasource } from '../../data/datasources/answer-key-api.datasource';
import { OfficialExamApiDatasource } from '../../data/datasources/official-exam-api.datasource';
import { OfficialExamMockDatasource } from '../../data/datasources/official-exam-mock.datasource';
import { OfficialExamRepositoryImpl } from '../../data/repositories/official-exam.repository.impl';

const answerKeyDatasource = new AnswerKeyApiDatasource();

/**
 * A prova oficial desta edição (as questões comentadas, da Aristo) e o
 * gabarito oficial (`answerKey`) que corrige o envio da pessoa.
 *
 * **O gabarito oficial vale primeiro o do Primum Core** (o que o admin
 * publicou): é com ele que o back calcula a nota e o ranking, então Gabarito e
 * Diagnóstico precisam ler a mesma fonte para os números baterem. Enquanto ele
 * não é publicado, vale o que vem na própria prova da Aristo (`correctChoice`,
 * anulação) — e, com mocks, só esse.
 *
 * O repositório é montado aqui, e não num container de módulo, porque o mock
 * precisa do tamanho da prova, que é da edição.
 */
export function useOfficialExam() {
  const { id, edition } = useTenant();

  const repository = useMemo(
    () =>
      new OfficialExamRepositoryImpl(
        USE_MOCKS
          ? new OfficialExamMockDatasource(edition)
          : new OfficialExamApiDatasource(),
      ),
    [edition],
  );

  const query = useQuery({
    queryKey: ['official-exam', id, edition.year, edition.officialTemplateId],
    queryFn: () => repository.getQuestions(edition.officialTemplateId),
    // A correção é publicada aos poucos: revalidar ao voltar para a aba é o
    // que faz o gabarito "atualizar" sem a pessoa recarregar.
    staleTime: 60_000,
  });

  const published = useQuery<AnswerKeyEntry[] | null>({
    queryKey: ['answer-key', id, edition.year],
    queryFn: () => answerKeyDatasource.getPublished(id, edition.year),
    enabled: !USE_MOCKS,
    staleTime: 60_000,
  });

  const answerKey = useMemo(() => {
    if (published.data) {
      // A dificuldade não vem sempre no publicado: completa pela da Aristo.
      const fromExam = query.data ? toAnswerKey(query.data) : [];
      const difficultyOf = new Map(
        fromExam.map((entry) => [entry.number, entry.difficulty]),
      );
      return published.data.map((entry) => ({
        ...entry,
        difficulty: entry.difficulty ?? difficultyOf.get(entry.number),
      }));
    }
    return query.data ? toAnswerKey(query.data) : undefined;
  }, [published.data, query.data]);

  return { ...query, answerKey };
}
